import { ChartXAxe, ChartYAxe } from 'chart.js';
import { DsChartCustomOptions } from '../interfaces/ds-chart-custom-options.interface';

/**
 * Returns Y Axis object for chart.
 *
 * @param customOptions Custom chart options
 * @param drawOnChartArea Whether to draw on chart area
 * @returns ChartYAxe
 */
export function getNewYAxis(customOptions: DsChartCustomOptions, drawOnChartArea = true): ChartYAxe {
  return {
    stacked: customOptions.stacked,
    ticks: {
      fontColor: customOptions.color,
      maxTicksLimit: customOptions.yMaxTicksLimit ? customOptions.yMaxTicksLimit : 10,
      stepSize: customOptions.xStepSize ? customOptions.xStepSize : 1,
    },
    gridLines: {
      display: customOptions.yGrid,
      drawOnChartArea,
      color: customOptions.color,
    },
  };
}

/**
 * Returns X axis object for time chart.
 *
 * @param displayGrid Whether to display X axis grid lines
 * @param color Font color of X axis ticks
 * @param dateFormat Format of date
 * @param customOptions the custom options
 * @returns ChartXAxe
 */
export function getNewTimeXAxis(customOptions: DsChartCustomOptions): ChartXAxe {
  return {
    type: 'time',
    position: 'bottom',
    time: {
      // display month/year on X axis using current locale
      tooltipFormat: 'DD-MM-YYYY',
      unit: customOptions.dateFormat,
      displayFormats: {
        day: 'LL',
      },
    },
    ticks: {
      // Limit number of X axis ticks
      autoSkip: false,
      maxTicksLimit: customOptions.xMaxTicksLimit ? customOptions.xMaxTicksLimit : 20,
      fontColor: customOptions.color,
      source: 'labels',
    },
    gridLines: {
      display: customOptions.xGrid,
    },
  };
}

/**
 * The returned object can replace the options.elements object,
 * to remove the Bezier curve tension between points.
 *
 * @param fill Whether to fill the area beneath the plots.
 * @param tension Bezier curve tension of the line. Set to 0 to draw straightlines.
 * @returns ChartElementsOptions
 */
export function getElements(fill: boolean, tension: number): Chart.ChartElementsOptions {
  return {
    line: {
      fill: fill || false,
      tension: tension || 0,
    },
  };
}

/**
 * Display Y axis value as the tooltip title.
 *
 * @returns yLabel
 */
export function getTooltipYLabelCallback() {
  return (tooltipItem: Chart.ChartTooltipItem, data: Chart.ChartData) => {
    return `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}`;
  };
}

/**
 * Returns date to string while taking account of locale ('1 november 2011' in english for instance).
 *
 * @param year the year
 * @param month the month
 * @param day the day
 * @returns string
 */
export function getLocaleDateString(year: number, month: number, day: number): string {
  const date = new Date(year, month - 1, day);
  const options = <const>{ year: 'numeric', month: '2-digit', day: '2-digit' };
  return date.toLocaleDateString(undefined, options);
}

/**
 * Returns an object containing year, month, day for a given date.
 *
 * @param date Given date
 * @returns year, month, day
 */
export function getDateInfo(date: Date): { year: string; month: string; day: string } {
  const month = date.getMonth() + 1;
  const mm: string = month < 10 ? `0${month}` : `${month}`;
  const dd: string = date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`;
  const yy = `${date.getFullYear()}`;
  return { year: yy, month: mm, day: dd };
}

/**
 * Returns short date to string (MM/YYYY).
 *
 * @param year the year
 * @param month the month
 * @returns string
 */
export function getShortDateString(year: number, month: number): string {
  const displayedMonth = month < 10 ? `0${month}` : `${month}`;
  return `${displayedMonth}/${year}`;
}

/**
 * Display tooltip labels as locale date.
 *
 * @param type the tooltip type
 * @returns DateString
 */
export function getDateTooltipLabelCallback(type?: 'long' | 'short') {
  return (tooltipItems) => {
    const splitDate = (tooltipItems[0].xLabel as string).split('-');
    switch (type) {
      case 'long':
        return getLocaleDateString(parseInt(splitDate[2], 10), parseInt(splitDate[1], 10), parseInt(splitDate[0], 10));
      case 'short':
        return getShortDateString(parseInt(splitDate[2], 10), parseInt(splitDate[1], 10));
      default:
        break;
    }
  };
}

/**
 * Get legend object.
 *
 * @param customOptions Optional custom options
 * @param usePointStyle Whether to display colored circles or squares
 * @returns ChartLegendLabelOptions
 */
export function getLegend(customOptions: DsChartCustomOptions): Chart.ChartLegendOptions {
  return {
    display: true,
    position: customOptions.legendPosition || 'top',
    align: customOptions.legendAlign || 'center',
    labels: {
      fontColor: customOptions.color,
      fontSize: customOptions.fontSize || 12,
      usePointStyle: true,
    },
  };
}
